(function initPersonalizationRenderer() {
    window.boxClubDocumentLoaded = false;

    const TEXT_BINDING_ATTRIBUTE = 'data-personalize-text';
    const ATTRIBUTE_BINDING_PREFIX = 'data-personalize-attr-';
    const TOKEN_PATTERN = /@(\w+)((?:\|(?:default:(?:"[^"]*"|'[^']*'|[^\s|]+)|\w+))*)/gi;

    // localStorage keys that are populated by setters elsewhere in this project.
    // A @token only falls back to localStorage when its key is on this allowlist,
    // so cached values only surface as page copy when we know something in the
    // theme actually wrote them. The trailing comment notes where each is set.
    // Structured/JSON cache keys (submissionCache, cart preview map, recently
    // viewed products, so_cache.*, loess:* toggles) are intentionally excluded
    // because they are not displayable text/attribute values.
    const KNOWN_STORAGE_KEYS = new Set([
        'dn',                      // initialize-setup.js, paw-print-custom.liquid
        'image_url',               // initialize-setup.js, paw-print-custom.liquid
        'dog_id',                  // cookies.js (setDogIDCookie), create-snippet-scripts.js
        'url_assignment_offer_id', // initialize-setup.js
        'campaign',                // cookies.js (setCampaignCookie)
        'c-url',                   // new-calendar.js, useCalendarOffer.js
        'tpp-rsid',                // paw-print-header.liquid
        'submission_identifier',   // paw-print-custom.liquid via setCookie()
        'user_name',               // gp-section-590234519164945354.liquid via setCookie()
        'pet_name',                // gp-section-590234519164945354.liquid via setCookie()
        'product_name',            // gp-section-590234519164945354.liquid via setCookie()
        'utm_source',              // gp-section-590234519164945354.liquid via setCookie()
        'utm_medium',              // gp-section-590234519164945354.liquid via setCookie()
        'utm_campaign',            // gp-section-590234519164945354.liquid via setCookie()
        'utm_content',             // gp-section-590234519164945354.liquid via setCookie()
        'utm_term',                // gp-section-590234519164945354.liquid via setCookie()
    ]);

    function transformValue(value, modifier) {
        if (!modifier) return value;

        switch (modifier.toLowerCase()) {
            case 'upper':
                return value.toUpperCase();
            case 'lower':
                return value.toLowerCase();
            case 'capitalize':
                return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
            case 'title':
                return value
                    .toLowerCase()
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            default:
                return value;
        }
    }

    // Treat blanks and the stringified "null"/"undefined" that careless setters
    // can leave behind as "no value", so we fall through to the default instead.
    function isUsableValue(value) {
        if (value === null || value === undefined) return false;
        const trimmed = String(value).trim();
        if (!trimmed) return false;
        const lowered = trimmed.toLowerCase();
        return lowered !== 'null' && lowered !== 'undefined';
    }

    function readFromStorage(key) {
        try {
            return window.localStorage.getItem(key);
        } catch (e) {
            // localStorage can throw (e.g. Safari private mode); treat as missing.
            return null;
        }
    }

    function stripQuotes(raw) {
        if (raw.length >= 2) {
            const first = raw.charAt(0);
            const last = raw.charAt(raw.length - 1);
            if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
                return raw.slice(1, -1);
            }
        }
        return raw;
    }

    // Parses a modifier chain like "|capitalize|default:'Valued Friend'" into an
    // ordered list of transforms plus an optional default value.
    function parseModifiers(chain) {
        const transforms = [];
        let defaultValue = null;
        if (!chain) return { transforms, defaultValue };

        const segmentRegex = /\|(default:(?:"[^"]*"|'[^']*'|[^\s|]+)|\w+)/gi;
        let match;
        while ((match = segmentRegex.exec(chain)) !== null) {
            const segment = match[1];
            if (/^default:/i.test(segment)) {
                defaultValue = stripQuotes(segment.slice('default:'.length));
            } else {
                transforms.push(segment);
            }
        }

        return { transforms, defaultValue };
    }

    // Resolution order: URL query param -> allowlisted localStorage value ->
    // default: modifier. Returns null when nothing resolves so the caller can
    // leave the original @token untouched (the pre-existing graceful behavior).
    function resolveToken(key, chain, replacements) {
        let value;
        if (replacements.has(key)) {
            value = replacements.get(key);
        } else if (KNOWN_STORAGE_KEYS.has(key)) {
            value = readFromStorage(key);
        }

        const { transforms, defaultValue } = parseModifiers(chain);

        if (isUsableValue(value)) {
            return transforms.reduce((acc, modifier) => transformValue(acc, modifier), value);
        }

        // The default is used verbatim (transforms only apply to dynamic values),
        // so authors get exactly the fallback copy they typed.
        if (defaultValue !== null) {
            return defaultValue;
        }

        return null;
    }

    function resolveExpression(expression, replacements) {
        let tokenCount = 0;
        let resolvedCount = 0;

        const value = String(expression).replace(TOKEN_PATTERN, (match, key, chain) => {
            tokenCount += 1;

            const resolved = resolveToken(key, chain, replacements);
            if (resolved === null) {
                return match;
            }

            resolvedCount += 1;
            return resolved;
        });

        return {
            value,
            fullyResolved: tokenCount > 0 && resolvedCount === tokenCount,
        };
    }

    function replaceTokensInString(str, replacements) {
        return resolveExpression(str, replacements).value;
    }

    function isBindingAttribute(attributeName) {
        return attributeName === TEXT_BINDING_ATTRIBUTE || attributeName.startsWith(ATTRIBUTE_BINDING_PREFIX);
    }

    function replaceBindingsInNode(node, replacements) {
        if (
            node.nodeType !== Node.ELEMENT_NODE ||
            ['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'TEXTAREA'].includes(node.tagName)
        ) {
            return;
        }

        const textExpression = node.getAttribute(TEXT_BINDING_ATTRIBUTE);
        if (textExpression !== null) {
            const result = resolveExpression(textExpression, replacements);
            if (result.fullyResolved) {
                node.textContent = result.value;
            }
        }

        // Copy the attributes because setting a resolved attribute mutates the
        // element's live NamedNodeMap while it is being inspected.
        for (const attr of Array.from(node.attributes)) {
            if (!attr.name.startsWith(ATTRIBUTE_BINDING_PREFIX)) {
                continue;
            }

            const targetAttribute = attr.name.slice(ATTRIBUTE_BINDING_PREFIX.length);
            if (!targetAttribute || targetAttribute.toLowerCase().startsWith('on')) {
                continue;
            }

            const result = resolveExpression(attr.value, replacements);
            if (result.fullyResolved) {
                node.setAttribute(targetAttribute, result.value);
            }
        }

        for (const child of node.childNodes) {
            replaceBindingsInNode(child, replacements);
        }
    }

    function replaceTokensInNode(node, replacements) {
        if (node.nodeType === Node.TEXT_NODE) {
            const newText = replaceTokensInString(node.nodeValue, replacements);
            if (newText !== node.nodeValue) {
                node.nodeValue = newText;
            }
            return;
        }

        if (
            node.nodeType !== Node.ELEMENT_NODE ||
            ['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'TEXTAREA'].includes(node.tagName)
        ) {
            return;
        }

        // Replace in attributes
        for (const attr of node.attributes) {
            // Binding expressions are reusable source templates. Do not replace
            // their tokens during the legacy raw-token DOM walk.
            if (isBindingAttribute(attr.name)) {
                continue;
            }

            const newValue = replaceTokensInString(attr.value, replacements);
            if (newValue !== attr.value) {
                node.setAttribute(attr.name, newValue);
            }
        }

        // Recurse
        for (const child of node.childNodes) {
            replaceTokensInNode(child, replacements);
        }
    }

    function getUrlReplacements() {
        const replacements = new Map();
        const urlParams = new URLSearchParams(window.location.search);

        for (const [key, value] of urlParams.entries()) {
            replacements.set(key, value);
        }

        return replacements;
    }

    function render(root = document.body) {
        if (!root) return;

        const replacements = getUrlReplacements();

        // Bindings contain valid fallback content in the initial HTML and only
        // replace it when every token in their expression resolves.
        replaceBindingsInNode(root, replacements);

        // Preserve the original raw-token behavior for existing theme content.
        replaceTokensInNode(root, replacements);
    }

    window.TPPPersonalization = {
        render,
    };

    function renderDocumentOnce() {
        if (window.boxClubDocumentLoaded) return;

        render(document.body);

        window.boxClubDocumentLoaded = true;
    }

    if (document.readyState === 'loading') {
        window.addEventListener('DOMContentLoaded', renderDocumentOnce);
    } else {
        renderDocumentOnce();
    }
})();
